import { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import { EUROPEAN_COUNTRY_CODES } from '../constants/europeanCountries'
import arrowLeft from '../assets/images/icons/arrow_left.png'
import arrowRight from '../assets/images/icons/arrow_right.png'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const fields = [
  ['name', 'vehicle_name'],
  ['licensePlate', 'vehicle_license_plate'],
  ['confirmLicensePlate', 'vehicle_license_plate_confirm'],
  ['vin', 'vehicle_vin'],
  ['brand', 'vehicle_brand'],
  ['model', 'vehicle_model'],
  ['variantType', 'vehicle_type'],
  ['vehicleType', 'vehicle_class'],
  ['category', 'vehicle_category'],
  ['fuelType', 'vehicle_fuel_type'],
  ['engineCapacity', 'vehicle_engine_capacity'],
  ['power', 'vehicle_power'],
  ['transmission', 'vehicle_transmission'],
  ['color', 'vehicle_color'],
  ['emissionsCo2', 'vehicle_emissions_co2'],
  ['consumption', 'vehicle_consumption'],
  ['emissionClass', 'vehicle_emission_class'],
  ['year', 'vehicle_year'],
  ['stkDate', 'vehicle_stk_date'],
  ['ekDate', 'vehicle_ek_date'],
  ['serviceDate', 'vehicle_service_date'],
  ['tiresAllSeason', 'vehicle_tires_all_season'],
  ['startDate', 'vehicle_start_date'],
  ['note', 'vehicle_note'],
  ['pzpDate', 'vehicle_pzp_date'],
  ['pzpFrequency', 'vehicle_pzp_frequency'],
  ['hasCasco', 'vehicle_has_casco'],
  ['cascoDate', 'vehicle_casco_date'],
  ['cascoFrequency', 'vehicle_casco_frequency'],
  ['registrationCountry', 'vehicle_registration_country'],
  ['usageType', 'vehicle_usage_type'], 
]

const DATE_FIELDS = [
  'stkDate',
  'ekDate',
  'serviceDate',
  'startDate',
  'pzpDate',
  'cascoDate'
]

const INTEGER_FIELDS = [
  'engineCapacity',
  'power',
  'year'
]

const DECIMAL_FIELDS = [
  'emissionsCo2',
  'consumption'
]

const REQUIRED_FIELDS = [
  'name',
  'licensePlate'
]

const formatForDateInput = (value) => {
  if (!value) return ''

  if (typeof value === 'string') {
    const normalized = value.trim()

    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      return normalized
    }

    const skMatch = normalized.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
    if (skMatch) {
      const [, day, month, year] = skMatch
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
  }

  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().split('T')[0]
}

const Row = ({ htmlFor, label, children, layout }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
    <label
      htmlFor={htmlFor}
      style={{
        width: layout.labelW,
        fontWeight: 700,
        fontSize: layout.labelFont
      }}
    >
      {label}
    </label>

    <div style={{ display: 'flex', alignItems: 'center', width: layout.inputW }}>
      {children}
    </div>
  </div>
)

const VehicleDetailModal = ({
  open,
  onClose,
  vehicle,
  onSaved,
  onPrevVehicle,
  onNextVehicle,
  hasPrevVehicle = false,
  hasNextVehicle = false
}) => {
const modalRef = useRef(null)
const { t } = useTranslation()

const getCountryLabel = (code) => {
  if (!code) return ''
  const name = t(code)
  const resolved = name && name !== code ? name : code
  return `${code} - ${resolved}`
}

  const [vw, setVw] = useState(window.innerWidth)
let layout = {}

if (vw >= 1920) {
  layout = {
    labelW: 300,
    inputW: 320,
    inputH: 20,
    labelFont: 15,
    inputFont: 14,
    rowGap: 4
  }
}
else if (vw >= 1366) {
  layout = {
    labelW: 260,
    inputW: 280,
    inputH: 18,
    labelFont: 14,
    inputFont: 13,
    rowGap: 3
  }
}
else {
  layout = {
    labelW: 220,
    inputW: 240,
    inputH: 18,
    labelFont: 13,
    inputFont: 12,
    rowGap: 3
  }
}

const inputStyle = {
  height: layout.inputH,
  fontSize: layout.inputFont,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #999',
  width: '100%'
}

  const [form, setForm] = useState({})
  const [original, setOriginal] = useState({})
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const showVehicleNavigation = Boolean(onPrevVehicle || onNextVehicle)

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const modalMaxWidth =
    vw >= 2800 ? '38vw' : vw >= 2600 ? '47.5vw' :  vw >= 2400 ? '51vw' :  vw >= 1920 ? '68vw' : vw >= 1366 ? '75vw' : '66vw'

  const modalMaxHeight =
    vw >= 1920 ? '85vh' : '95vh'

  const modalTitleFontSize =
    vw >= 1920 ? 24 : vw >= 1366 ? 24 : 18

useEffect(() => {
  if (!open || !vehicle) return
  setForm({
  ...vehicle,
  confirmLicensePlate: ''
})
  setOriginal(vehicle)
  setEditMode(false)
  setMessage(null)
  setErrors({})
}, [open, vehicle])

useEffect(() => {
  if (!open) return

  const trapModalFocus = (e) => {
    if (e.key !== 'Tab') return false

    const modalEl = modalRef.current
    if (!modalEl) return false

    const focusable = Array.from(
      modalEl.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true')

    if (!focusable.length) {
      e.preventDefault()
      modalEl.focus()
      return true
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    const isInsideModal = modalEl.contains(active)

    if (e.shiftKey) {
      if (!isInsideModal || active === first) {
        e.preventDefault()
        last.focus()
        return true
      }
    } else if (!isInsideModal || active === last) {
      e.preventDefault()
      first.focus()
      return true
    }

    return false
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (editMode) {
        setEditMode(false)
      } else {
        onClose()
      }
      return
    }

    if (trapModalFocus(e)) return

    if (e.key === 'ArrowLeft' && hasPrevVehicle) {
      if (editMode) return
      e.preventDefault()
      onPrevVehicle?.()
      return
    }

    if (e.key === 'ArrowRight' && hasNextVehicle) {
      if (editMode) return
      e.preventDefault()
      onNextVehicle?.()
    }
  }

  window.addEventListener('keydown', handleKeyDown)
  return () => window.removeEventListener('keydown', handleKeyDown)
}, [open, editMode, onClose, hasPrevVehicle, hasNextVehicle, onPrevVehicle, onNextVehicle])

useEffect(() => {
  if (!open) return

  const modalEl = modalRef.current
  if (!modalEl) return

  const focusable = modalEl.querySelector(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  if (focusable && typeof focusable.focus === 'function') {
    focusable.focus()
    return
  }

  modalEl.focus()
}, [open, editMode])

  const vehicleId = useMemo(() => vehicle?.id, [vehicle])

  const mid = Math.ceil(fields.length / 2)
  const column1 = fields.slice(0, mid)
  const column2 = fields.slice(mid)

const validate = () => {

  const newErrors = {}

  // REQUIRED základné polia
  REQUIRED_FIELDS.forEach(field => {
    if (!form[field] || String(form[field]).trim() === '') {
      newErrors[field] = true
    }
  })

// kontrola či sa menila EČV
const licensePlateChanged =
  (form.licensePlate || '').toUpperCase() !==
  (original.licensePlate || '').toUpperCase()

// REQUIRED confirm EČV len keď sa mení
if (licensePlateChanged && !form.confirmLicensePlate) {
  newErrors.confirmLicensePlate = true
}

  // ak sú required chyby → STOP
  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors)
    setMessage({ message: t('TRIPLOG.FORM.REQUIRED_FIELDS'), type: 'error' })
    return false
  }

  // MATCH kontrola (až keď sú vyplnené)
if (
  licensePlateChanged &&
  form.licensePlate.toUpperCase() !== form.confirmLicensePlate.toUpperCase()
) {
    setErrors(prev => ({
      ...prev,
      licensePlate: true,
      confirmLicensePlate: true
    }))
    setMessage({ message: t('vehicle_license_plate_missmatch'), type: 'error' })
    return false
  }

  return true
}

  const saveVehicle = async () => {
    if (!vehicleId) return
    if (!validate()) return

const { confirmLicensePlate, ...rest } = form

const payload = {
  ...rest,
  ...(rest.hasCasco === false ? { cascoDate: null } : {}),
  ...Object.fromEntries(
    DATE_FIELDS.map((key) => [
      key,
      rest[key] ? formatForDateInput(rest[key]) || null : null
    ])
  )
}

    try {
      setLoading(true)
      const res = await fetchWithAuth(`/vehicle/${vehicleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
        let backendError = null

        try {
          backendError = await res.json()
        } catch {
          backendError = null
        }

        if (
          res.status === 409 &&
          (backendError?.field === 'vin' || backendError?.errorCode === 'vehicle_duplicate_vin')
        ) {
          setMessage({ message: t('vehicle_duplicate_vin'), type: 'error' })
          return
        }

        if (
          res.status === 409 &&
          (
            backendError?.field === 'licensePlate' ||
            backendError?.errorCode === 'vehicle_duplicate_plate'
          )
        ) {
          setMessage({ message: t('vehicle_duplicate_plate'), type: 'error' })
          return
        }

        setMessage({ message: backendError?.message || t('vehicle_save_failed'), type: 'error' })
        return
      }

      const updated = await res.json().catch(() => payload)
      setOriginal(updated)
      setForm({
  ...updated,
  confirmLicensePlate: ''
})
      setEditMode(false)
//setMessage({ message: t('SAVED'), type: 'success' })
      onSaved?.(updated)
    } catch {
      setMessage({ message: t('CONNECTION_ERROR'), type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!open || !vehicle) return null

  const renderField = ([key, labelKey]) => {

if (key === 'confirmLicensePlate' && !editMode) {
  return null
}
    const value = form[key]

const isDateField = DATE_FIELDS.includes(key)

const errorStyle = errors[key]
  ? { background: 'rgba(255,0,0,0.1)', border: '1px solid #c62828' }
  : { background: '#fff' }

const displayValue =
  isDateField && value
    ? new Date(value).toLocaleDateString('sk-SK')
    : (key === 'pzpFrequency' || key === 'cascoFrequency') && value
      ? (() => {
          const freq = Number(value)
          if (freq === 1) return t('vehicle_payment_yearly')
          if (freq === 2) return t('vehicle_payment_halfyear')
          if (freq === 4) return t('vehicle_payment_quarterly')
          return value
        })()
: key === 'usageType'
  ? (value === 'firemne'
      ? t('vehicle_usage_type_company')
      : value === 'sukromne'
        ? t('vehicle_usage_type_private')
        : '')
      : key === 'registrationCountry' && value
        ? getCountryLabel(value)
        : key === 'tiresAllSeason' || key === 'hasCasco'
          ? (value ? t('dropdown_yes') : t('dropdown_no'))
          : value || ''

    return (
      <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
        {key === 'note' ? (
          <textarea
            id={key}
            value={form[key] || ''}
            disabled={!editMode}
onChange={e => {
  setForm(prev => ({
    ...prev,
    [key]: e.target.value === '' ? null : e.target.value
  }))
  setErrors(prev => ({ ...prev, [key]: false }))
}}
            rows={2}
            style={{
              ...inputStyle,
              resize: 'vertical',
  	      fontFamily: 'Arial, sans-serif',
              background: editMode ? '#fff' : '#ddd'
            }}
          />
) : (key === 'pzpFrequency' || key === 'cascoFrequency') && editMode ? (
<select
  id={key}
  value={form[key] ?? ''}
  disabled={key === 'cascoFrequency' && !form.hasCasco}
  onChange={e => {
      setForm(prev => ({ ...prev, [key]: Number(e.target.value) }))
      setErrors(prev => ({ ...prev, [key]: false }))
    }}
style={{
  ...inputStyle,
  ...errorStyle,
  height: 32,
  ...(key === 'cascoFrequency' && !form.hasCasco
    ? { background: '#ddd', cursor: 'not-allowed' }
    : {})
}}
  >
    <option value="">-</option>
    <option value={1}>{t('vehicle_payment_yearly')}</option>
    <option value={2}>{t('vehicle_payment_halfyear')}</option>
    <option value={4}>{t('vehicle_payment_quarterly')}</option>
  </select>

) : key === 'registrationCountry' && editMode ? (
<select
  id={key}
  value={form[key] ?? 'SK'}
  onChange={e => {
    setForm(prev => ({ ...prev, [key]: e.target.value }))
    setErrors(prev => ({ ...prev, [key]: false }))
  }}
  style={{
    ...inputStyle,
    ...(errors.registrationCountry
      ? { background: 'rgba(255,0,0,0.1)', border: '1px solid #c62828' }
      : { background: '#fff' }),
    height: 32
  }}
>
  {['SK', ...EUROPEAN_COUNTRY_CODES.filter(c => c !== 'SK')].map(code => (
    <option key={code} value={code}>
      {getCountryLabel(code)}
    </option>
  ))}
</select>
) : key === 'usageType' && editMode ? (
<select
  id={key}
  value={form[key] ?? 'sukromne'}
  onChange={e => {
    setForm(prev => ({
      ...prev,
      [key]: e.target.value
    }))
    setErrors(prev => ({ ...prev, [key]: false }))
  }}
  style={{
    ...inputStyle,
    ...errorStyle,
    height: 32
  }}
>
  <option value="sukromne">{t('vehicle_usage_type_private')}</option>
  <option value="firemne">{t('vehicle_usage_type_company')}</option>
</select>
) : key === 'hasCasco' && editMode ? (
<select
  id={key}
  value={form[key] === true ? 'true' : 'false'}
  onChange={e => {
    setForm(prev => {
      const value = e.target.value === 'true'
      return {
        ...prev,
        [key]: value,
        ...(value ? {} : { cascoDate: null })
      }
    })
    setErrors(prev => ({ ...prev, [key]: false }))
  }}
  style={{
    ...inputStyle,
    ...errorStyle,
    height: 32
  }}
>
  <option value="true">{t('dropdown_yes')}</option>
  <option value="false">{t('dropdown_no')}</option>
</select>
) : key === 'tiresAllSeason' && editMode ? (
<select
  id={key}
  value={form[key] === true ? 'true' : 'false'}
  onChange={e => {
    setForm(prev => ({
      ...prev,
      [key]: e.target.value === 'true'
    }))
    setErrors(prev => ({ ...prev, [key]: false }))
  }}
  style={{
    ...inputStyle,
    ...errorStyle,
    height: 32
  }}
>
  <option value="true">{t('dropdown_yes')}</option>
  <option value="false">{t('dropdown_no')}</option>
</select>
) : (
<input
  id={key}
  type={isDateField && editMode ? 'date' : 'text'}
  value={
    editMode
      ? (isDateField
          ? formatForDateInput(form[key])
          : (form[key] || '')
        )
      : (displayValue || '')
  }
disabled={
  !editMode ||
  (key === 'cascoDate' && form.hasCasco === false)
}
onChange={e => {
  let val = e.target.value

  // CAPITALIZE pre ŠPZ + VIN
  if (
    key === 'licensePlate' ||
    key === 'confirmLicensePlate' ||
    key === 'vin'
  ) {
    val = val.toUpperCase()
  }

if (INTEGER_FIELDS.includes(key)) {

  if (key === 'year') {
    if (!/^\d{0,4}$/.test(val)) {
      return
    }
  } else {
    if (!/^\d*$/.test(val)) {
      return
    }
  }

}

  if (DECIMAL_FIELDS.includes(key)) {
    val = val.replace(',', '.')
    if (!/^\d*\.?\d{0,2}$/.test(val)) {
      return
    }
  }

  setForm(prev => ({ ...prev, [key]: val }))
  setErrors(prev => ({ ...prev, [key]: false }))
}}
onKeyDown={e => {
  if (!editMode) return

  if (isDateField) {
    if (e.key === 'Tab') {
      e.preventDefault()

      const modalEl = modalRef.current
      if (!modalEl) return

      const focusable = Array.from(
        modalEl.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.disabled)

      const index = focusable.indexOf(e.target)

      if (e.shiftKey) {
        focusable[index - 1]?.focus()
      } else {
        focusable[index + 1]?.focus()
      }

      return
    }

    if (e.key.length === 1) {
      e.preventDefault()
    }
  }
}}
  onClick={e => {
    if (isDateField && editMode && e.target.showPicker) {
      e.target.showPicker()
    }
  }}
  style={{
    ...inputStyle,
    ...(editMode && !(key === 'cascoDate' && form.hasCasco === false)
      ? errorStyle
      : { background: '#ddd', border: inputStyle.border }),
    cursor: isDateField && editMode ? 'pointer' : 'default'
  }}
/>
)}
      </Row>
    )
  }

  return (
    <>
      <Overlay open={loading && false} zIndex={4000} backdropOpacity={0.75} />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3100,
          padding: 20
        }}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          onClick={e => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: '5px 30px 10px 30px',
            border: '2px solid #107c6b',
            borderRadius: 14,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: '100%',
            maxWidth: modalMaxWidth,
            maxHeight: modalMaxHeight,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
<div
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 5,
    marginBottom: 20
  }}
>
  <h3
    style={{
      fontSize: modalTitleFontSize,
      margin: 0
    }}
  >
    {t('vehicle_detail_title')}
  </h3>

  {message && (
    <MessageBox
      message={message.message}
      type={message.type}
    />
  )}
</div>

          <div
            style={{
              display: 'flex',
              gap: 60,
              overflowY: 'auto',
              flex: 1
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: layout.rowGap }}>
              {column1.map(renderField)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {column2.map(renderField)}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 15,
              justifyContent: 'center'
            }}
          >
            {!editMode ? (
              <>
                {showVehicleNavigation && (
<button
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'prevVehicle',
      hoverBtn === 'prevVehicle'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasPrevVehicle ? 0.4 : 1,
    cursor: !hasPrevVehicle ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasPrevVehicle}
  onMouseEnter={() => setHoverBtn('prevVehicle')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('prevVehicle')}
  onMouseUp={() => setPressedBtn(null)}
  onClick={() => onPrevVehicle?.()}
>
  <img
    src={arrowLeft}
    alt="prev"
    style={{
      width: 18,
      height: 18,
      pointerEvents: 'none',
      filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
    }}
  />
</button>
                )}
                <button
                  style={modalPrimaryButtonStyle(
                    pressedBtn,
                    'editVehicle',
                    hoverBtn === 'editVehicle'
                  )}
                  onMouseEnter={() => setHoverBtn('editVehicle')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('editVehicle')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={() => setEditMode(true)}
                >
                  {t('EDIT_PROFILE')}
                </button>

                <button
                  style={modalSecondaryButtonStyle(
                    pressedBtn,
                    'closeVehicle',
                    hoverBtn === 'closeVehicle'
                  )}
                  onMouseEnter={() => setHoverBtn('closeVehicle')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('closeVehicle')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={onClose}
                >
                  {t('CLOSE')}
                </button>
                {showVehicleNavigation && (
<button
  style={{
    ...modalSecondaryButtonStyle(
      pressedBtn,
      'nextVehicle',
      hoverBtn === 'nextVehicle'
    ),
    width: 50,
    minWidth: 40,
    padding: 0,
    opacity: !hasNextVehicle ? 0.4 : 1,
    cursor: !hasNextVehicle ? 'not-allowed' : 'pointer'
  }}
  disabled={!hasNextVehicle}
  onMouseEnter={() => setHoverBtn('nextVehicle')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('nextVehicle')}
  onMouseUp={() => setPressedBtn(null)}
  onClick={() => onNextVehicle?.()}
>
  <img
    src={arrowRight}
    alt="next"
    style={{
      width: 18,
      height: 18,
      pointerEvents: 'none',
      filter: 'invert(33%) sepia(24%) saturate(746%) hue-rotate(120deg) brightness(85%) contrast(90%)'
    }}
  />
</button>
                )}
              </>
            ) : (
              <>
                <button
                  style={modalPrimaryButtonStyle(
                    pressedBtn,
                    'saveVehicle',
                    hoverBtn === 'saveVehicle'
                  )}
                  onMouseEnter={() => setHoverBtn('saveVehicle')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('saveVehicle')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={saveVehicle}
                >
                  {t('SAVE')}
                </button>

                <button
                  style={modalSecondaryButtonStyle(
                    pressedBtn,
                    'cancelVehicle',
                    hoverBtn === 'cancelVehicle'
                  )}
                  onMouseEnter={() => setHoverBtn('cancelVehicle')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('cancelVehicle')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={() => {
                    setForm({
  ...original,
  confirmLicensePlate: ''
})
                    setErrors({})
                    setEditMode(false)
                  }}
                >
                  {t('CANCEL')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default VehicleDetailModal